import { useEffect } from 'react';

/** How long "Saved" stays on screen before the Save button comes back. */
const CLEAR_SAVED_AFTER_MS = 1500;

/**
 * Clears a mutation's success state a moment after it succeeds, so the Save button returns
 * without the user reloading. Matches RestaurantInformationSettings, and replaces the same eight
 * lines copy-pasted across the four storefront editor screens.
 *
 * **The `saved` guard is the point.** Written inline, the effect scheduled its timer on MOUNT as
 * well, because `resetUpdate` is stable and the effect therefore ran once with `saved === false`.
 * A user who pressed Save 1.4s after the screen opened had `reset()` called mid-flight: the
 * mutation dropped back to idle, the spinner vanished, the Save button reappeared as if nothing
 * had happened, and the "Saved" that followed never rendered. Returning early when there is
 * nothing to clear is what keeps the timer tied to the success it is clearing.
 */
export function useClearSavedState(saved: boolean, reset: () => void): void {
  useEffect(() => {
    if (!saved) {
      return;
    }
    const timer = setTimeout(() => {
      reset();
    }, CLEAR_SAVED_AFTER_MS);
    return () => clearTimeout(timer);
  }, [saved, reset]);
}
